import React from 'react';
import { motion } from 'framer-motion';
import { FaRupeeSign, FaWhatsapp, FaArrowLeft, FaEdit } from 'react-icons/fa';
import { calculateFare, formatCurrency, getVehicleDisplayName, convertTo12HourFormat } from '../utils/helpers';
import { sendTelegramNotification } from '../services/telegramNotification';
import './EstimationPage.css';

const EstimationPage = ({ bookingData, onBack, onModify, onNewBooking }) => {
  const { distance } = bookingData;
  const fareDetails = calculateFare(distance, bookingData.vehicleType, bookingData.tripType, bookingData.pickupDate, bookingData.returnDate);
  
  // Scroll to top when component mounts
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  

  const handleBookNow = async () => {
    // Create WhatsApp message with booking details
    const message = `Hi! I would like to book a taxi with the following details:

📍 From: ${bookingData.pickupLocation}
📍 To: ${bookingData.dropoffLocation}
📅 Pickup Date: ${bookingData.pickupDate}
${bookingData.tripType === 'roundtrip' && bookingData.returnDate ? `📅 Return Date: ${bookingData.returnDate}\n` : ''}🕐 Time: ${convertTo12HourFormat(bookingData.pickupTime)}
👤 Name: ${bookingData.customerName}
📞 Phone: ${bookingData.customerPhone}
🚗 Vehicle: ${getVehicleDisplayName(bookingData.vehicleType)}
🛣️ Trip Type: ${bookingData.tripType === 'oneway' ? 'One Way' : `Round Trip (${fareDetails.numberOfDays} day${fareDetails.numberOfDays > 1 ? 's' : ''})`}
📏 Distance: ${distance} km
💰 Estimated Fare: ${formatCurrency(fareDetails.totalFare)}

Note: Toll fees, hills charges, Parking charges and Inter-State Permit charges (if any) are extra.

Please confirm my booking. Thank you!`;

    const phoneNumber = '7200085345';
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    
    // Send Telegram notification to admin
    try {
      const bookingDataWithFare = {
        ...bookingData,
        estimatedFare: fareDetails.totalFare
      };
      await sendTelegramNotification(bookingDataWithFare);
      console.log('Admin notified via Telegram - Book Now');
    } catch (telegramError) {
      console.warn('Telegram notification failed:', telegramError);
      // Continue with booking even if Telegram fails
    }
    
    // Show success message
    alert('Booking request sent! You will be redirected to WhatsApp to confirm your booking.');
    
    // Redirect to WhatsApp
    window.open(whatsappUrl, '_blank');
  };

  return (
    <motion.div 
      className="estimation-page-container"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="estimation-card">
        {/* Header */}
        <div className="estimation-header">
          <button onClick={onBack} className="back-button">
            <FaArrowLeft />
          </button>
          <div className="header-content">
            <FaRupeeSign className="btn-icon" />
            <h2 className="estimation-title">Fare Estimation</h2>
          </div>
        </div>

        {/* Trip Summary */}
        <div className="trip-summary">
          <h3 className="section-title">Trip Details</h3>
          <div className="summary-grid-2x3">
            <div className="summary-item-styled">
              <span className="summary-label">From:</span>
              <span className="summary-value">{bookingData.pickupLocation}</span>
            </div>
            <div className="summary-item-styled">
              <span className="summary-label">To:</span>
              <span className="summary-value">{bookingData.dropoffLocation}</span>
            </div>
            <div className="summary-item-styled">
              <span className="summary-label">
                {bookingData.tripType === 'roundtrip' ? 'Pickup Date:' : 'Date:'}
              </span>
              <span className="summary-value">{bookingData.pickupDate}</span>
            </div>
            {bookingData.tripType === 'roundtrip' && bookingData.returnDate && (
              <div className="summary-item-styled">
                <span className="summary-label">Return Date:</span>
                <span className="summary-value">{bookingData.returnDate}</span>
              </div>
            )}
            <div className="summary-item-styled">
              <span className="summary-label">Time:</span>
              <span className="summary-value">{convertTo12HourFormat(bookingData.pickupTime)}</span>
            </div>
            <div className="summary-item-styled">
              <span className="summary-label">Passenger:</span>
              <span className="summary-value">{bookingData.customerName}</span>
            </div>
            <div className="summary-item-styled">
              <span className="summary-label">Phone:</span>
              <span className="summary-value">{bookingData.customerPhone}</span>
            </div>
          </div>
        </div>

        {/* Fare Breakdown */}
        <div className="fare-breakdown">
          <h3 className="section-title">Fare Breakdown</h3>
          <div className="fare-details">
            <div className="fare-item">
              <span className="fare-label">
                {bookingData.tripType === 'roundtrip' ? 'One Way Distance:' : 'Distance:'}
              </span>
              <span className="fare-value">{distance} km</span>
            </div>
            {bookingData.tripType === 'roundtrip' && (
              <div className="fare-item">
                <span className="fare-label">Total Distance (Round Trip):</span>
                <span className="fare-value">{fareDetails.effectiveDistance} km</span>
              </div>
            )}
            <div className="fare-item">
              <span className="fare-label">Vehicle:</span>
              <span className="fare-value">{getVehicleDisplayName(bookingData.vehicleType)}</span>
            </div>
            <div className="fare-item">
              <span className="fare-label">Trip Type:</span>
              <span className="fare-value">
                {bookingData.tripType === 'oneway' ? 'One Way' : 'Round Trip'}
              </span>
            </div>
            <div className="fare-item">
              <span className="fare-label">Rate per km:</span>
              <span className="fare-value">₹{fareDetails.rate}</span>
            </div>
            <div className="fare-item">
              <span className="fare-label">
                Base Fare ({fareDetails.effectiveDistance || distance} km × ₹{fareDetails.rate}):
              </span>
              <span className="fare-value">{formatCurrency(fareDetails.baseFare)}</span>
            </div>
            <div className="fare-item">
              <span className="fare-label">
                Driver Bata ({fareDetails.numberOfDays} day{fareDetails.numberOfDays > 1 ? 's' : ''} × ₹{fareDetails.dailyDriverBata}):
              </span>
              <span className="fare-value">{formatCurrency(fareDetails.driverBata)}</span>
            </div>
            <div className="fare-divider"></div>
            <div className="fare-item total">
              <span className="fare-label">Total Estimated Fare:</span>
              <span className="fare-value">{formatCurrency(fareDetails.totalFare)}</span>
            </div>
          </div>
        </div>

        {/* Important Notes */}
        <div className="fare-notes">
          <h4 className="notes-title">Important Notes:</h4>
          <ul className="notes-list">
            <li>Toll fees and Inter-State Permit charges (if any) are extra.</li>
            {bookingData.tripType === 'roundtrip' && (
              <>
                <li>Round Trip: Distance calculated as {distance} km × 2 = {fareDetails.effectiveDistance} km total</li>
                <li>Round Trip: Minimum running must be 250kms per day. For Bengaluru it is minimum 300kms per day.</li>
              </>
            )}
            {bookingData.tripType === 'oneway' && (
              <li>One Way: Minimum running must be 130 kms per day.</li>
            )}
            <li>Driver bata is ₹{fareDetails.dailyDriverBata} per day ({fareDetails.numberOfDays} day{fareDetails.numberOfDays > 1 ? 's' : ''} = ₹{fareDetails.driverBata}).</li>
            <li>Final fare may vary based on actual route and traffic conditions.</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="estimation-actions">
          <button
            onClick={onModify}
            className="btn btn-modify"
          >
            <FaEdit className="btn-icon" />
            Modify Booking
          </button>
          <button
            onClick={handleBookNow}
            className="btn btn-book"
          >
            <FaWhatsapp className="btn-icon" />
            Book Now
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default EstimationPage;
